import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Crown, ShieldCheck, ListChecks, FileCheck2, Plus, Edit3,
  Trash2, Save, X, Power, PowerOff, Search, Library as LibraryIcon, History,
  BookOpen, FileDown,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import PhotoUploader from "@/components/PhotoUploader";
import ClauseIndexCardDialog from "@/components/ClauseIndexCardDialog";

/**
 * MasterLibrary — Master-Controller UI to curate the shared catalog:
 * Compliance Clauses, V&V Templates, Checklist Templates.
 *
 * Code defaults are shown read-only with a Disable/Enable toggle. Custom
 * entries created here are fully editable and deletable, and are merged into
 * the same consumer endpoints (`/compliance/coverage`, `/template-library/*`).
 */
export default function MasterLibrary() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("clauses");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {type, mode:'new'|'edit', doc}
  const [viewClauseId, setViewClauseId] = useState(null);

  const load = () => {
    api.get("/master/library/catalog").then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-10 font-mono text-sm text-[#1a1a1a]/50">Loading library…</div>;

  const toggle = async (collection, key, disabled) => {
    try {
      await api.post(`/master/library/toggle/${collection}`, { key, disabled });
      toast.success(disabled ? "Disabled" : "Enabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (type, id) => {
    if (!confirm("Delete this custom entry permanently?")) return;
    try {
      const url = type === "clauses" ? `/master/library/clauses/${id}`
        : type === "vv_templates" ? `/master/library/vv-templates/${id}`
        : `/master/library/ck-templates/${id}`;
      await api.delete(url);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-4 lg:p-8 space-y-5" data-testid="master-library-page">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Link to="/app/master" className="btn-back" data-testid="ml-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Master
        </Link>
        <div className="flex-1" />
      </div>

      {/* Hero */}
      <section className="relative hero-navy rounded-3xl p-6 lg:p-8 text-white overflow-hidden">
        <div className="absolute inset-0 dotted opacity-25" />
        <div className="absolute -top-16 -right-8 w-72 h-72 rounded-full bg-[#ff4500]/25 blur-3xl" />
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-3 py-1 backdrop-blur-sm">
              <Crown className="w-3.5 h-3.5" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">Master Controller · Library Editor</span>
            </div>
            <h1 className="mt-3 font-display font-black text-3xl lg:text-4xl leading-tight tracking-tight">Catalog Curation.</h1>
            <p className="mt-2 text-sm text-white/75 max-w-xl">
              Add or disable clauses, V&amp;V templates and Checklist templates for the entire platform. Changes go live immediately for every org.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              ["Clauses", data.counts.clauses_total, "text-[#a13ad6]"],
              ["V&V", data.counts.vv_total, "text-[#ff4500]"],
              ["Checklists", data.counts.ck_total, "text-[#1a5fb4]"],
            ].map(([l, v, c]) => (
              <div key={l}>
                <div className="text-[10px] font-mono uppercase tracking-wider text-white/60">{l}</div>
                <div className={`font-display font-black text-2xl ${c}`}>{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs + search + new */}
      <div className="flex items-center flex-wrap gap-2 justify-between">
        <div className="flex border border-[#1a1a1a]/20 bg-white rounded-full p-1">
          {[
            ["clauses", "Clauses", ShieldCheck],
            ["vv_templates", "V&V Templates", FileCheck2],
            ["ck_templates", "Checklist Templates", ListChecks],
            ["changelog", "Changelog", History],
          ].map(([k, l, Icon]) => (
            <button key={k} data-testid={`ml-tab-${k}`} onClick={() => setTab(k)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider font-bold rounded-full inline-flex items-center gap-1.5 ${tab === k ? "bg-[#1a1a1a] text-white" : "text-[#1a1a1a]/70"}`}>
              <Icon className="w-3 h-3" /> {l}
            </button>
          ))}
        </div>
        {tab !== "changelog" && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 bg-white border border-[#1a1a1a]/10 rounded-full px-3 py-1.5 shadow-soft">
              <Search className="w-3.5 h-3.5 text-[#1a1a1a]/50" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
                data-testid="ml-search"
                className="bg-transparent outline-none text-sm w-52 placeholder:text-[#1a1a1a]/40" />
            </div>
            <button onClick={() => setEditing({ type: tab, mode: "new", doc: null })}
              data-testid="ml-new"
              className="btn-pill btn-pill-primary">
              <Plus className="w-3.5 h-3.5" /> New custom
            </button>
          </div>
        )}
      </div>

      {/* List */}
      {tab === "clauses" && (
        <ClauseList
          codes={data.code_clauses} customs={data.custom_clauses}
          disabled={new Set(data.disabled_clauses)} search={search}
          onToggle={(k, d) => toggle("clauses", k, d)}
          onView={(id) => setViewClauseId(id)}
          onEdit={(doc) => setEditing({ type: "clauses", mode: "edit", doc })}
          onDelete={(id) => remove("clauses", id)} />
      )}
      {tab === "vv_templates" && (
        <VVList
          codes={data.code_vv} customs={data.custom_vv}
          disabled={new Set(data.disabled_vv)} search={search}
          onToggle={(k, d) => toggle("vv_templates", k, d)}
          onEdit={(doc) => setEditing({ type: "vv_templates", mode: "edit", doc })}
          onDelete={(key) => remove("vv_templates", key)} />
      )}
      {tab === "ck_templates" && (
        <CkList
          codes={data.code_ck} customs={data.custom_ck}
          disabled={new Set(data.disabled_ck)} search={search}
          onToggle={(k, d) => toggle("ck_templates", k, d)}
          onEdit={(doc) => setEditing({ type: "ck_templates", mode: "edit", doc })}
          onDelete={(key) => remove("ck_templates", key)} />
      )}
      {tab === "changelog" && <ChangelogView />}

      {editing && (
        <EditDialog editing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      <ClauseIndexCardDialog clauseId={viewClauseId} onClose={() => setViewClauseId(null)} />
    </div>
  );
}

/* ═══════════════════════════ Sub-components ═══════════════════════════ */

function Row({ children, disabled, isCode, ...rest }) {
  return (
    <div {...rest}
      className={`surface-card rounded-xl p-4 flex items-start gap-3 ${disabled ? "opacity-50" : ""}`}>
      <div className={`w-1 self-stretch rounded ${isCode ? "bg-[#1a1a1a]/40" : "bg-[#ff4500]"}`} />
      {children}
    </div>
  );
}

function ClauseList({ codes, customs, disabled, search, onToggle, onView, onEdit, onDelete }) {
  const filtered = [...codes, ...customs].filter((c) =>
    !search || (c.title + JSON.stringify(c.citations || [])).toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-2">
      <LegendBar />
      {filtered.map((c) => (
        <Row key={c.id} isCode={c.source === "code"} disabled={disabled.has(c.id)}
          data-testid={`ml-clause-${c.id}`}>
          <button onClick={() => onView(c.id)} type="button" data-testid={`ml-clause-view-${c.id}`}
            className="flex-1 min-w-0 text-left cursor-pointer hover:opacity-80 transition-opacity">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              {c.source === "code" ? (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#1a1a1a]/10 text-[#1a1a1a]/70">Code default</span>
              ) : (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#ff4500]/15 text-[#ff4500]">Custom</span>
              )}
              {(c.citations || []).map((cit, i) => (
                <span key={i} className="font-mono text-[9px] uppercase tracking-wider text-[#1a1a1a]/60">
                  {cit.framework} {cit.ref}
                </span>
              ))}
              <span className="text-[9px] uppercase tracking-wider text-[#1a1a1a]/40">· {c.category}</span>
              {(c.attachments || []).length > 0 && (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#fff1ea] text-[#ff4500] flex items-center gap-1">
                  <FileDown className="w-2.5 h-2.5" /> {c.attachments.length} ref
                </span>
              )}
            </div>
            <div className="font-display font-bold text-sm hover:text-[#ff4500]">{c.title}</div>
            {c.description && (
              <div className="text-[11px] text-[#1a1a1a]/65 mt-0.5 line-clamp-2">{c.description}</div>
            )}
            <div className="mt-1.5 flex flex-wrap gap-1.5 text-[10px]">
              <MetaChip label="V&V templates" count={(c.vv_keys || []).length} color="#a13ad6"
                items={c.vv_keys || []} />
              <MetaChip label="Checklist templates" count={(c.checklist_keys || []).length} color="#1a5fb4"
                items={c.checklist_keys || []} />
              <MetaChip label="V&V sub-tasks" count={(c.vv_sub_tasks || []).length} color="#a13ad6"
                items={(c.vv_sub_tasks || []).map((s) => s.title)} />
              <MetaChip label="Checklist items" count={(c.checklist_items || []).length} color="#1a5fb4"
                items={(c.checklist_items || []).map((s) => s.title)} />
              <MetaChip label="Keywords" count={(c.keywords || []).length} color="#1a8754"
                items={c.keywords || []} />
            </div>
          </button>
          <RowActions
            source={c.source} isDisabled={disabled.has(c.id)}
            onView={() => onView(c.id)}
            onToggle={() => onToggle(c.id, !disabled.has(c.id))}
            onEdit={() => onEdit(c)}
            onDelete={() => onDelete(c.id)} />
        </Row>
      ))}
    </div>
  );
}

function LegendBar() {
  return (
    <div className="bg-[#f7f5f2] border border-[#1a1a1a]/10 rounded-xl p-3 text-[11px] flex flex-wrap gap-x-5 gap-y-1 items-center">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] font-bold">// Legend</span>
      <span><b className="text-[#a13ad6]">V&V templates</b> — linked V&V programs that satisfy this clause</span>
      <span><b className="text-[#1a5fb4]">Checklist templates</b> — audits that satisfy this clause</span>
      <span><b className="text-[#a13ad6]">V&V sub-tasks</b> — inline verification/validation checks</span>
      <span><b className="text-[#1a5fb4]">Checklist items</b> — inline audit questions</span>
      <span><b className="text-[#1a8754]">Keywords</b> — matching terms for auto-coverage</span>
    </div>
  );
}

function MetaChip({ label, count, color, items = [] }) {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-mono text-[10px]"
      style={{ borderColor: color + "40", color, background: color + "0d" }}
      title={items.slice(0, 8).join(" · ") + (items.length > 8 ? ` … +${items.length - 8}` : "")}>
      <b>{count}</b> {label}
    </span>
  );
}

function VVList({ codes, customs, disabled, search, onToggle, onEdit, onDelete }) {
  const filtered = [...codes, ...customs].filter((t) =>
    !search || (t.title + " " + t.category).toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-2">
      {filtered.map((t) => (
        <Row key={t.key} isCode={t.source === "code"} disabled={disabled.has(t.key)}
          data-testid={`ml-vv-${t.key}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              {t.source === "code" ? (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#1a1a1a]/10 text-[#1a1a1a]/70">Code default</span>
              ) : (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#ff4500]/15 text-[#ff4500]">Custom</span>
              )}
              <span className="font-mono text-[9px] uppercase tracking-wider text-[#1a1a1a]/50">{t.key}</span>
              <span className="text-[9px] uppercase tracking-wider text-[#1a1a1a]/50">· {t.category} · {t.schedule_type}</span>
            </div>
            <div className="font-display font-bold text-sm">{t.title}</div>
            <div className="text-[11px] text-[#1a1a1a]/60 mt-0.5 line-clamp-1">{t.description}</div>
            <div className="text-[10px] font-mono text-[#1a1a1a]/45 mt-1">
              V: {(t.sub_tasks || []).filter((s) => s.section === "verification").length} · V&apos;: {(t.sub_tasks || []).filter((s) => s.section === "validation").length}
            </div>
          </div>
          <RowActions
            source={t.source} isDisabled={disabled.has(t.key)}
            onToggle={() => onToggle(t.key, !disabled.has(t.key))}
            onEdit={t.source === "custom" ? () => onEdit(t) : null}
            onDelete={t.source === "custom" ? () => onDelete(t.key) : null} />
        </Row>
      ))}
    </div>
  );
}

function CkList({ codes, customs, disabled, search, onToggle, onEdit, onDelete }) {
  const filtered = [...codes, ...customs].filter((t) =>
    !search || (t.name + " " + t.category).toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-2">
      {filtered.map((t) => (
        <Row key={t.key} isCode={t.source === "code"} disabled={disabled.has(t.key)}
          data-testid={`ml-ck-${t.key}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              {t.source === "code" ? (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#1a1a1a]/10 text-[#1a1a1a]/70">Code default</span>
              ) : (
                <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#ff4500]/15 text-[#ff4500]">Custom</span>
              )}
              <span className="font-mono text-[9px] uppercase tracking-wider text-[#1a1a1a]/50">{t.key}</span>
              <span className="text-[9px] uppercase tracking-wider text-[#1a1a1a]/50">· {t.category} · {(t.items || []).length} items</span>
            </div>
            <div className="font-display font-bold text-sm">{t.name}</div>
            <div className="text-[11px] text-[#1a1a1a]/60 mt-0.5 line-clamp-1">{t.description}</div>
            <div className="text-[10px] font-mono text-[#1a1a1a]/45 mt-1">
              {(t.tags || []).map((tg) => `#${tg}`).join(" ")}
            </div>
          </div>
          <RowActions
            source={t.source} isDisabled={disabled.has(t.key)}
            onToggle={() => onToggle(t.key, !disabled.has(t.key))}
            onEdit={t.source === "custom" ? () => onEdit(t) : null}
            onDelete={t.source === "custom" ? () => onDelete(t.key) : null} />
        </Row>
      ))}
    </div>
  );
}

function RowActions({ source, isDisabled, onView, onToggle, onEdit, onDelete }) {
  return (
    <div className="flex gap-1.5 flex-shrink-0">
      {onView && (
        <button onClick={onView} title="Preview Index Card" data-testid="row-view"
          className="p-1.5 rounded-lg bg-[#1a5fb4]/10 text-[#1a5fb4] hover:bg-[#1a5fb4]/20">
          <BookOpen className="w-3.5 h-3.5" />
        </button>
      )}
      <button onClick={onToggle}
        title={isDisabled ? "Enable" : "Disable"}
        className={`p-1.5 rounded-lg ${isDisabled ? "bg-[#1a1a1a]/8 text-[#1a1a1a]/60" : "bg-[#1a8754]/10 text-[#1a8754]"}`}>
        {isDisabled ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
      </button>
      {onEdit && (
        <button onClick={onEdit} data-testid="row-edit" className="p-1.5 rounded-lg bg-[#ff4500]/10 text-[#ff4500]">
          <Edit3 className="w-3.5 h-3.5" />
        </button>
      )}
      {onDelete && (
        <button onClick={onDelete} className="p-1.5 rounded-lg bg-red-50 text-red-500">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

/* ─── Edit dialog ─────────────────────────────────────────────────────── */
function EditDialog({ editing, onClose, onSaved }) {
  const isNew = editing.mode === "new";
  const [form, setForm] = useState(() => initialForm(editing));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      let body;
      let url;
      if (editing.type === "clauses") {
        body = {
          title: form.title, category: form.category,
          description: form.description || "",
          tips: form.tips || "",
          citations: form.citations, vv_keys: strListToArr(form.vv_keys),
          checklist_keys: strListToArr(form.checklist_keys),
          keywords: strListToArr(form.keywords),
          vv_sub_tasks: (form.vv_sub_tasks || []).filter((s) => (s.title || "").trim()),
          checklist_items: (form.checklist_items || []).filter((s) => (s.title || "").trim()),
          attachments: (form.attachments || []).filter((a) => a.file_id && (a.title || "").trim()),
        };
        url = isNew ? "/master/library/clauses" : `/master/library/clauses/${form.id}`;
      } else if (editing.type === "vv_templates") {
        body = {
          key: form.key, category: form.category, title: form.title,
          description: form.description, schedule_type: form.schedule_type,
          sub_tasks: form.sub_tasks,
        };
        url = isNew ? "/master/library/vv-templates" : `/master/library/vv-templates/${form.key}`;
      } else {
        body = {
          key: form.key, category: form.category, name: form.name,
          description: form.description, tags: strListToArr(form.tags),
          items: form.items,
        };
        url = isNew ? "/master/library/ck-templates" : `/master/library/ck-templates/${form.key}`;
      }
      if (isNew) await api.post(url, body); else await api.patch(url, body);
      toast.success(isNew ? "Created" : "Updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight flex items-center gap-2">
            <LibraryIcon className="w-5 h-5 text-[#ff4500]" />
            {isNew ? "New " : "Edit "}
            {editing.type === "clauses" ? "clause" : editing.type === "vv_templates" ? "V&V template" : "Checklist template"}
          </DialogTitle>
          <DialogDescription className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/60">
            {isNew ? "Adds a custom entry that merges into the global catalog" : "Edit this custom entry"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[60vh] overflow-auto">
          {editing.type === "clauses" && <ClauseForm form={form} setForm={setForm} />}
          {editing.type === "vv_templates" && <VVForm form={form} setForm={setForm} isNew={isNew} />}
          {editing.type === "ck_templates" && <CkForm form={form} setForm={setForm} isNew={isNew} />}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-ghost" data-testid="ml-edit-cancel">
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button onClick={submit} disabled={busy} data-testid="ml-edit-save"
            className="btn-pill btn-pill-primary disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function strListToArr(s) { return (s || "").split(",").map((x) => x.trim()).filter(Boolean); }

function initialForm(editing) {
  if (editing.mode === "edit") {
    const d = editing.doc;
    if (editing.type === "clauses") {
      return {
        id: d.id, title: d.title, category: d.category, citations: d.citations || [],
        vv_keys: (d.vv_keys || []).join(", "), checklist_keys: (d.checklist_keys || []).join(", "),
        keywords: (d.keywords || []).join(", "),
        vv_sub_tasks: d.vv_sub_tasks || [],
        checklist_items: d.checklist_items || [],
        description: d.description || "",
        attachments: d.attachments || [],
        tips: d.tips || "",
      };
    }
    if (editing.type === "vv_templates") {
      return { key: d.key, category: d.category, title: d.title, description: d.description || "",
        schedule_type: d.schedule_type || "annual", sub_tasks: d.sub_tasks || [] };
    }
    return { key: d.key, category: d.category, name: d.name, description: d.description || "",
      tags: (d.tags || []).join(", "), items: d.items || [] };
  }
  // New
  if (editing.type === "clauses") return {
    title: "", category: "Management",
    citations: [{ framework: "sqf", ref: "" }],
    vv_keys: "", checklist_keys: "", keywords: "",
    vv_sub_tasks: [], checklist_items: [], description: "",
    attachments: [], tips: "",
  };
  if (editing.type === "vv_templates") return { key: "", category: "Management", title: "", description: "", schedule_type: "annual", sub_tasks: [] };
  return { key: "", category: "GMP", name: "", description: "", tags: "", items: [] };
}

function ClauseForm({ form, setForm }) {
  const addCit = () => setForm({ ...form, citations: [...form.citations, { framework: "sqf", ref: "" }] });
  const setCit = (i, k, v) => setForm({ ...form, citations: form.citations.map((c, j) => j === i ? { ...c, [k]: v } : c) });
  const rmCit = (i) => setForm({ ...form, citations: form.citations.filter((_, j) => j !== i) });

  const vvSubs = form.vv_sub_tasks || [];
  const setVvSubs = (fn) => setForm({ ...form, vv_sub_tasks: fn(vvSubs) });
  const ckItems = form.checklist_items || [];
  const setCkItems = (fn) => setForm({ ...form, checklist_items: fn(ckItems) });

  return (
    <>
      <Section title="1 · Identify the clause" hint="What compliance requirement does this represent?">
        <Field label="Clause title" hint="Short, plain-English name auditors will recognise" testid="ml-form-title">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Food Defense Threat Assessment"
            className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
        </Field>
        <Field label="Category" hint="Broad program area (Management, GMP, Food Safety, HR, etc.)">
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            placeholder="Management"
            className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
        </Field>
        <Field label="Description" hint="Optional — plain explanation of what compliance requires">
          <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2} placeholder="What the auditor is looking for when they open this clause…"
            className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm resize-none" />
        </Field>
      </Section>

      <Section title="2 · Cite the standard" hint="Which standards, at which reference numbers, cover this clause?">
        <div className="space-y-1.5">
          {form.citations.map((c, i) => (
            <div key={i} className="flex gap-1.5 flex-wrap">
              <select value={c.framework} onChange={(e) => setCit(i, "framework", e.target.value)}
                className="border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value="sqf">SQF</option>
                <option value="iso22000">ISO 22000</option>
                <option value="fsma">FSMA</option>
                <option value="brc">BRCGS</option>
                <option value="haccp">HACCP</option>
                <option value="iso9001">ISO 9001</option>
                <option value="iso14001">ISO 14001</option>
                <option value="iso45001">ISO 45001</option>
              </select>
              <input placeholder="Reference, e.g. 2.9 or §9.3.2" value={c.ref} onChange={(e) => setCit(i, "ref", e.target.value)}
                className="flex-1 min-w-[120px] border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm" />
              {c.framework === "sqf" && (
                <select value={c.code || "food_manufacturing"} onChange={(e) => setCit(i, "code", e.target.value)}
                  className="border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm bg-white"
                  data-testid={`ml-form-sqf-code-${i}`}>
                  <option value="food_manufacturing">Food Mfg</option>
                  <option value="food_packaging">Food Packaging Mfg</option>
                  <option value="primary_plant">Primary Plant</option>
                  <option value="primary_animal">Primary Animal</option>
                  <option value="storage_distribution">Storage & Dist.</option>
                  <option value="retail_food">Retail Food</option>
                  <option value="quality_code">Quality Code</option>
                </select>
              )}
              <button onClick={() => rmCit(i)} className="p-1.5 text-red-500"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={addCit} className="text-[10px] uppercase tracking-wider text-[#ff4500] font-bold">+ Add citation</button>
        </div>
      </Section>

      <Section title="3 · Verification & Validation sub-tasks"
        hint='The actual actions your team performs to PROVE this clause is met. "Verification" = the program is done as written. "Validation" = what is written actually works.'>
        <div className="space-y-1.5">
          {vvSubs.map((s, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              <select value={s.section}
                onChange={(e) => setVvSubs((arr) => arr.map((x, j) => j === i ? { ...x, section: e.target.value } : x))}
                className="border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value="verification">Verification</option>
                <option value="validation">Validation</option>
              </select>
              <input value={s.title} placeholder="e.g. Review pest-control log for the quarter"
                onChange={(e) => setVvSubs((arr) => arr.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                className="flex-1 border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm" />
              <button onClick={() => setVvSubs((arr) => arr.filter((_, j) => j !== i))} className="p-1.5 text-red-500">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button onClick={() => setVvSubs((arr) => [...arr, { title: "", section: "verification" }])}
            className="text-[10px] uppercase tracking-wider text-[#a13ad6] font-bold">
            + Add V&V sub-task
          </button>
        </div>
      </Section>

      <Section title="4 · Checklist items"
        hint="Individual audit questions (pass/fail or written) that provide floor-level evidence for this clause.">
        <div className="space-y-1.5">
          {ckItems.map((s, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              <select value={s.kind}
                onChange={(e) => setCkItems((arr) => arr.map((x, j) => j === i ? { ...x, kind: e.target.value } : x))}
                className="border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value="passfail">Pass/Fail</option>
                <option value="written">Written</option>
                <option value="rank">Rank 1-5</option>
              </select>
              <input value={s.title} placeholder="e.g. Perimeter fence intact, no gaps > 4 in."
                onChange={(e) => setCkItems((arr) => arr.map((x, j) => j === i ? { ...x, title: e.target.value } : x))}
                className="flex-1 border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm" />
              <button onClick={() => setCkItems((arr) => arr.filter((_, j) => j !== i))} className="p-1.5 text-red-500">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button onClick={() => setCkItems((arr) => [...arr, { title: "", kind: "passfail" }])}
            className="text-[10px] uppercase tracking-wider text-[#1a5fb4] font-bold">
            + Add checklist item
          </button>
        </div>
      </Section>

      <Section title="5 · Reference documents (Index Card)"
        hint="PDFs, screenshots, or images that appear on the clause's Index Card — e.g. an external lumen-standards table auditors can consult.">
        <ClauseAttachmentsEditor
          attachments={form.attachments || []}
          onChange={(atts) => setForm({ ...form, attachments: atts })} />
      </Section>

      <Section title="6 · Tips · How to fulfill this clause"
        hint="Practical guidance for auditors and managers. Shown on the Index Card below the description.">
        <textarea value={form.tips || ""} onChange={(e) => setForm({ ...form, tips: e.target.value })}
          rows={3} placeholder="Best-practice guidance, common pitfalls, what evidence looks like…"
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm resize-none" />
      </Section>

      <Section title="7 · Advanced linkage (optional)"
        hint="Only needed if you're linking to existing V&V or Checklist templates in the library. Most users can leave these blank.">
        <Field label="V&V template keys" hint="Comma-separated template keys, e.g. pest_control, food_defense">
          <input value={form.vv_keys} onChange={(e) => setForm({ ...form, vv_keys: e.target.value })}
            placeholder="pest_control, food_defense"
            className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
        </Field>
        <Field label="Checklist template keys" hint="Comma-separated checklist keys, e.g. gmp_hygiene, receiving">
          <input value={form.checklist_keys} onChange={(e) => setForm({ ...form, checklist_keys: e.target.value })}
            placeholder="gmp_hygiene, receiving"
            className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
        </Field>
        <Field label="Keywords" hint="Comma-separated terms used to auto-match this clause to V&V items and checklists by title">
          <input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })}
            placeholder="pest, rodent, IPM, exterminator"
            className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
        </Field>
      </Section>
    </>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="border-l-2 border-[#ff4500]/40 pl-4 py-2 space-y-2">
      <div>
        <div className="font-display font-bold text-[13px] text-[#1a1a1a]">{title}</div>
        {hint && <div className="text-[11px] text-[#1a1a1a]/60 mt-0.5">{hint}</div>}
      </div>
      {children}
    </div>
  );
}


function ClauseAttachmentsEditor({ attachments, onChange }) {
  const add = () => onChange([...(attachments || []), { title: "", description: "", file_id: "" }]);
  const set = (i, patch) => onChange(attachments.map((a, j) => j === i ? { ...a, ...patch } : a));
  const rm = (i) => onChange(attachments.filter((_, j) => j !== i));

  return (
    <div className="space-y-2">
      {(attachments || []).map((a, i) => (
        <div key={i} className="border border-[#1a1a1a]/12 rounded-xl p-3 bg-white space-y-2"
          data-testid={`clause-att-editor-${i}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1 space-y-2">
              <input value={a.title} onChange={(e) => set(i, { title: e.target.value })}
                placeholder="Title (e.g. IESNA Illuminance Table for Food Processing)"
                className="w-full border border-[#1a1a1a]/20 rounded-lg px-2.5 py-1.5 text-sm font-medium" />
              <textarea value={a.description || ""} onChange={(e) => set(i, { description: e.target.value })}
                rows={2} placeholder="Description — what source, why it matters, what to look for."
                className="w-full border border-[#1a1a1a]/20 rounded-lg px-2.5 py-1.5 text-[12px] resize-none" />
            </div>
            <button onClick={() => rm(i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded" type="button">
              <X className="w-4 h-4" />
            </button>
          </div>
          {a.file_id ? (
            <div className="flex items-center gap-2 text-[11px] font-mono text-[#1a8754]">
              <FileDown className="w-3 h-3" />
              File attached ({a.file_id.slice(0, 8)}…)
              <button onClick={() => set(i, { file_id: "" })}
                className="text-[10px] uppercase tracking-wider text-[#1a1a1a]/60 hover:text-red-500 ml-2" type="button">
                remove file
              </button>
            </div>
          ) : (
            <PhotoUploader
              photoIds={[]}
              onChange={(ids) => { if (ids.length) set(i, { file_id: ids[0] }); }}
              testIdPrefix={`clause-att-upload-${i}`}
              accept="application/pdf,image/*" />
          )}
        </div>
      ))}
      <button onClick={add} type="button"
        data-testid="clause-att-add"
        className="text-[10px] uppercase tracking-wider text-[#ff4500] font-bold">
        + Add reference document
      </button>
    </div>
  );
}

function VVForm({ form, setForm, isNew }) {
  const addSub = () => setForm({ ...form, sub_tasks: [...form.sub_tasks, { title: "", section: "verification" }] });
  const setSub = (i, k, v) => setForm({ ...form, sub_tasks: form.sub_tasks.map((s, j) => j === i ? { ...s, [k]: v } : s) });
  const rmSub = (i) => setForm({ ...form, sub_tasks: form.sub_tasks.filter((_, j) => j !== i) });
  return (
    <>
      <Field label="Key (unique, slug)">
        <input disabled={!isNew} value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
          placeholder="e.g. my_custom_program"
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm disabled:bg-[#f7f5f2]" />
      </Field>
      <Field label="Title">
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Category"><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" /></Field>
        <Field label="Frequency">
          <select value={form.schedule_type} onChange={(e) => setForm({ ...form, schedule_type: e.target.value })}
            className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm bg-white">
            {["once", "monthly", "quarterly", "semi_annual", "annual"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Description">
        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <Field label={`Sub-tasks (${form.sub_tasks.length})`}>
        <div className="space-y-1.5">
          {form.sub_tasks.map((s, i) => (
            <div key={i} className="flex gap-1.5">
              <select value={s.section} onChange={(e) => setSub(i, "section", e.target.value)}
                className="border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value="verification">Verification</option>
                <option value="validation">Validation</option>
              </select>
              <input value={s.title} onChange={(e) => setSub(i, "title", e.target.value)}
                placeholder="Sub-task title"
                className="flex-1 border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm" />
              <button onClick={() => rmSub(i)} className="p-1.5 text-red-500"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={addSub} className="text-[10px] uppercase tracking-wider text-[#ff4500] font-bold">+ Add sub-task</button>
        </div>
      </Field>
    </>
  );
}

function CkForm({ form, setForm, isNew }) {
  const addItem = () => setForm({ ...form, items: [...form.items, { title: "", kind: "passfail" }] });
  const setItem = (i, k, v) => setForm({ ...form, items: form.items.map((s, j) => j === i ? { ...s, [k]: v } : s) });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, j) => j !== i) });
  return (
    <>
      <Field label="Key (unique, slug)">
        <input disabled={!isNew} value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm disabled:bg-[#f7f5f2]" />
      </Field>
      <Field label="Name">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Category"><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" /></Field>
        <Field label="Tags (comma-separated)"><input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" /></Field>
      </div>
      <Field label="Description">
        <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <Field label={`Items (${form.items.length})`}>
        <div className="space-y-1.5">
          {form.items.map((it, i) => (
            <div key={i} className="flex gap-1.5">
              <select value={it.kind} onChange={(e) => setItem(i, "kind", e.target.value)}
                className="border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value="passfail">P/F</option>
                <option value="written">Text</option>
                <option value="rank">1-5</option>
                <option value="multiple_choice">MC</option>
              </select>
              <input value={it.title} onChange={(e) => setItem(i, "title", e.target.value)}
                placeholder="Item title"
                className="flex-1 border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-sm" />
              <button onClick={() => rmItem(i)} className="p-1.5 text-red-500"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <button onClick={addItem} className="text-[10px] uppercase tracking-wider text-[#ff4500] font-bold">+ Add item</button>
        </div>
      </Field>
    </>
  );
}

function Field({ label, children, testid, hint }) {
  return (
    <div data-testid={testid}>
      <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10px] text-[#1a1a1a]/55 mt-1 leading-relaxed">{hint}</div>}
    </div>
  );
}

const ACTION_COLORS = {
  create: "#1a8754", update: "#d6a900", delete: "#ff4500",
  disable: "#1a1a1a", enable: "#1a5fb4",
};

function ChangelogView() {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const url = filter === "all" ? "/master/library/changelog" : `/master/library/changelog?collection=${filter}`;
    api.get(url).then((r) => setEntries(r.data || [])).catch(() => setEntries([]));
  }, [filter]);

  return (
    <div className="space-y-3" data-testid="master-changelog">
      <div className="flex flex-wrap gap-2">
        {[["all", "All"], ["clauses", "Clauses"], ["vv_templates", "V&V"], ["ck_templates", "Checklists"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
            data-testid={`ml-cl-filter-${k}`}
            className={`text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full ${filter === k ? "bg-[#1a1a1a] text-white" : "bg-white border border-[#1a1a1a]/20 text-[#1a1a1a]/70"}`}>
            {l}
          </button>
        ))}
        <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 self-center">
          {entries.length} entries
        </span>
      </div>
      {entries.length === 0 && (
        <div className="surface-card rounded-2xl py-12 text-center text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
          No changes recorded yet
        </div>
      )}
      <div className="space-y-1.5">
        {entries.map((e) => (
          <div key={e.id} data-testid={`ml-cl-entry-${e.id}`}
            className="surface-card rounded-lg p-3 flex items-start gap-3">
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded"
              style={{ background: `${ACTION_COLORS[e.action] || "#1a1a1a"}18`, color: ACTION_COLORS[e.action] || "#1a1a1a" }}>
              {e.action}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="font-mono font-bold text-[#1a1a1a]">{e.collection}</span>
                <span className="font-mono text-[#ff4500]">{e.key}</span>
                <span className="text-[#1a1a1a]/50">by {e.actor_email}</span>
              </div>
              <div className="text-[10px] font-mono text-[#1a1a1a]/40 mt-0.5">{e.at}</div>
              {(e.before || e.after) && (
                <details className="mt-1">
                  <summary className="text-[10px] uppercase tracking-wider text-[#ff4500] cursor-pointer font-bold">Show diff</summary>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-[10px] font-mono">
                    <pre className="bg-red-50 p-2 rounded overflow-auto max-h-40">{JSON.stringify(e.before || null, null, 2)}</pre>
                    <pre className="bg-green-50 p-2 rounded overflow-auto max-h-40">{JSON.stringify(e.after || null, null, 2)}</pre>
                  </div>
                </details>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
